import { FC, useContext, useEffect, useSyncExternalStore } from 'react';
import { getHotelDisplayModeSettings } from 'constants/constant';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { FilterModel } from 'ts/hotel/classes/FilterModel';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import {
  applyAssistantFilters,
  finishSearchLoading,
  getPendingFiltersVersion,
  peekPendingFilters,
  subscribeToPendingFilters,
  takePendingFilters,
} from 'utils/assistantChatStore';
import { highlightAssistantTargets } from 'utils/assistantHighlight';

interface AssistantPageBridgeProps {
  onChangeFilter: (filter: FilterModel) => void;
  onSortChange?: (sort: string) => void;
  currentSortBy?: string;
}

/**
 * The part of the AI Assistant that lives inside the results page. It renders nothing.
 *
 * The chat can be anywhere in the app (the floating widget is on every page), but the page's filter lives here, so the
 * chat only queues what the assistant wants changed (assistantChatStore) and this applies it to the filter:
 *  - filters sent while the page has a filter go in at once;
 *  - filters sent with a new search, or while the page has no filter (it clears its filter when a search starts and
 *    rebuilds it as hotels arrive), wait until the rebuilt filter has hotels, then go in once;
 *  - the loader of a search the assistant opened ends when the page has hotels.
 * It is mounted whenever the results page is, whichever tab of the left panel is showing, so none of this depends on
 * a chat component being on screen.
 */
const AssistantPageBridge: FC<AssistantPageBridgeProps> = ({
  onChangeFilter,
  onSortChange,
  currentSortBy,
}) => {
  const { filter } = useContext(HotelFilterContext);
  const { hotelIndex } = useContext(HotelConfigContext);
  const { userSiteConfig, setUserSiteConfig } = useContext(MainContext);
  const chatKey = hotelIndex ?? 0;
  const queued = useSyncExternalStore(subscribeToPendingFilters, getPendingFiltersVersion);

  const onDisplayModeChange = (value: number) => {
    const settings = getHotelDisplayModeSettings(value);
    if (!settings) {
      return;
    }

    const nextConfig = {
      ...userSiteConfig,
      hotel: {
        ...userSiteConfig.hotel,
        ...settings,
      },
    };

    setUserSiteConfig(nextConfig);

    try {
      const userLocal = JSON.parse(localStorage.getItem('user') || '{}');
      userLocal.config = nextConfig;
      localStorage.setItem('user', JSON.stringify(userLocal));
    } catch {
      // Config still applies for this session without localStorage.
    }
  };

  const applyPending = (pendingFilter: FilterModel) => {
    const pending = takePendingFilters(chatKey);
    const result = applyAssistantFilters(pendingFilter, pending, {
      onChangeFilter,
      onSortChange,
      onDisplayModeChange,
      currentSortBy,
      currentDisplayMode: userSiteConfig?.hotel?.displayMode,
    });
    if (result.changedKinds.length) {
      highlightAssistantTargets(result.changedKinds);
    }
  };

  // Something was just queued.
  useEffect(() => {
    const pending = peekPendingFilters(chatKey);
    if (!pending || pending.waitForHotels) {
      return;
    }
    if (!filter) {
      pending.waitForHotels = true;
      return;
    }
    applyPending(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued]);

  // The page's filter changed: if it has hotels now, the filters that were waiting for them go in.
  // (The page clears its filter when a search starts, so a filter left over from the previous search never counts.)
  useEffect(() => {
    if (!filter || !((filter.price?.max ?? 0) > 0)) {
      return;
    }
    if (peekPendingFilters(chatKey)) {
      applyPending(filter);
    }
    // The page has hotels again: the search the assistant opened has arrived, so its loader is done.
    if (finishSearchLoading(chatKey)) {
      highlightAssistantTargets(['list']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return null;
};

export default AssistantPageBridge;
